<template>
  <section
    :class="[
      'collection-detail',
      !isLoading &&
        isVulnerabilityColl &&
        `collection-detail--vulnerabilities-bg-1`,
    ]"
    :style="{ 'padding-top': `${totalCollapsedNavbarHeight + 50}px` }"
  >
    <div
      v-if="!isLoading && isLockedContent"
      class="collection-detail__locked-badge"
      :style="{ top: `${totalCollapsedNavbarHeight}px` }"
    >
      <LockIcon size="16" />
      {{ getCollectionLockedLabel() }}
      <a
        :class="[
          'collection-detail__locked-badge-link',
          isLoading && 'collection-detail__locked-badge-link--disabled',
        ]"
        @click="getAccess"
      >
        Get access
      </a>
    </div>
    <div
      v-if="!isLoading && partiallyLockedContent"
      class="collection-detail__locked-badge collection-detail__locked-badge-partial"
      :style="{ top: `${totalCollapsedNavbarHeight}px` }"
    >
      <LockIcon size="16" />
      <div>
        Unlock all labs! To access this complete collection,
        <a
          :class="[
            'collection-detail__locked-badge-link',
            isLoading && 'collection-detail__locked-badge-link--disabled',
          ]"
          @click="getAccess"
        >
          upgrade Skill Dive
        </a>
        to include {{ getCollectionPartiallyLockedLabel() }}
      </div>
    </div>
    <ActionLoader v-if="isLoading" class="collection-detail__loader" />
    <div v-else class="collection-detail__container">
      <div class="collection-detail__navigation">
        <router-link
          :to="{ name: 'skill-dive' }"
          class="collection-detail__navigation-back-link btn btn--tertiary btn--default-icon-color"
        >
          <ArrowLeftIcon class="collection-detail__navigation-back-link-icon" />
          <span>Back to Skill Dive</span>
        </router-link>
      </div>
      <div class="collection-detail__header">
        <div class="collection-detail__header-title">
          {{ collection.name }}
        </div>
        <div class="collection-detail__header-subtitle">
          <div
            v-if="isVulnerabilityColl"
            class="collection-detail__header-subtitle-disclaimer"
          >
            New content every month!
          </div>
          <PillBadge
            translucid
            sober
            class="collection-detail__header-subtitle-labs"
          >
            <strong>{{ `${totalLabs} ` }}</strong>
            HANDS-ON LABS
          </PillBadge>
          <SeparatorBorder
            vertical
            height="30px"
            class="collection-detail__header-subtitle-separator--desktop-only"
          />
          <div class="collection-detail__header-subtitle-details">
            <Difficulty
              class="collection-detail__header-subtitle-difficulty"
              :difficulty="difficulty"
              alwaysVisible
            />
            <div
              class="collection-detail__header-subtitle-learning-area-wrapper"
            >
              <div
                v-for="name in collectionLearningAreas"
                :key="`${name}-la`"
                class="collection-detail__header-subtitle-learning-area-item"
              >
                <SeparatorBorder
                  vertical
                  height="30px"
                  class="collection-detail__header-subtitle-separator"
                />
                <LearningAreaLabel
                  :learningArea="name"
                  class="collection-detail__header-subtitle-learning-area"
                  showIcon
                  uppercase
                />
              </div>
            </div>
          </div>
        </div>
        <div class="collection-detail__header-description">
          {{ description }}
        </div>
      </div>
      <div class="collection-detail__search">
        <SearchInput
          sortingDropdown
          :sortingOptions="sortingOptions"
          :sortingSelected="sortingSelected"
          :filterLocally="false"
          class="collection-detail__search-input"
          @handle-sorting="updateSort"
        />
      </div>
      <div class="collection-detail__results-label">
        <span> {{ labsWorkerState.totalItems }} results </span>
        <span v-if="stateQuery">for "{{ stateQuery }}"</span>
      </div>
      <div class="collection-detail__filter-pills">
        <FilterTags
          :currentRefinements="labsWorkerState.activeFilters"
          :filterMap="filterMap"
          @remove-filter="updateFilterAndSearch"
          @clear-filters="clearFilters"
        />
      </div>
      <div
        v-if="labsWorkerState.items.length"
        class="collection-detail__content"
      >
        <template v-if="isVulnerabilityColl">
          <button
            class="collection-detail__content-filters-btn btn btn--primary btn--icon btn--small btn--default-icon-color"
            :class="[
              isContentFiltered &&
                'collection-detail__content-filters-btn--filtered',
            ]"
            @click="isFiltersModalOpen = true"
          >
            <FilterIcon size="16" />
          </button>

          <div
            class="collection-detail__content-filters-container"
            :class="[
              isFiltersModalOpen &&
                'collection-detail__content-filters-container--modal-open',
            ]"
          >
            <div class="collection-detail__content-filters-mobile-header">
              <button
                class="collection-detail__content-filters-mobile-header__back-btn btn btn--tertiary btn--default-icon-color"
                @click="isFiltersModalOpen = false"
              >
                <ArrowLeftIcon
                  size="25"
                  class="collection-detail__content-filters-mobile-header__back-btn__icon"
                />
                <span> Filter Results </span>
              </button>
              <button
                class="collection-detail__content-filters-mobile-header__clear-btn btn btn--tertiary"
                @click="clearFilters"
              >
                <span> Clear filters </span>
              </button>
            </div>
            <ContentFilters
              :filters="labsWorkerState.filterCounts"
              :filterMap="filterMap"
              :currentRefinements="labsWorkerState.activeFilters"
              @update-filters="updateFilterAndSearch"
            />
          </div>
        </template>
        <ul class="collection-detail__content-labs-list-ul">
          <SkillDiveLabRow
            v-for="item in labsWorkerState.items"
            ref="labRows"
            :key="item.uuid"
            :content="item"
            :learningAreasAllowed="availableLearningAreas"
            :isolatedContent="false"
            @tag-selected="
              ({ name }) =>
                updateFilterAndSearch({ facet: 'tags.name', value: name })
            "
          />
        </ul>
      </div>
      <div v-else class="collection-detail__content-labs-list-empty">
        <img
          class="collection-detail__content-labs-list-empty__img"
          src="@/assets/images/status/fail-low.png"
          alt="NO RESULTS"
        />
        <h5 class="collection-detail__content-labs-list-empty__error">
          Oops! We couldn't find any results matching your search.
        </h5>
        <p class="collection-detail__content-labs-list-empty__instruction">
          Please try searching for something else.
        </p>
      </div>
      <FeaturedCollectionsSection
        v-if="relatedCollections.length"
        class="collection-detail__related-collections"
        :featuredCollections="relatedCollections.slice(0, 3)"
      />
    </div>
  </section>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import SkillDiveLabRow from '@/components/lab-experience/SkillDiveLabRow.vue'
import Difficulty from '@/components/properties/Difficulty.vue'
import PillBadge from '@/components/shared/PillBadge.vue'
import { ArrowLeftIcon, LockIcon, FilterIcon } from 'vue-feather-icons'
import SearchInput from '@/components/shared/SearchInput.vue'
import {
  collectionCommons,
  licenseAddonCommons,
  windowResize,
} from '@/helpers/mixins'
import algoliasearch from 'algoliasearch'
import LearningAreaLabel from '@/components/properties/LearningAreaLabel.vue'
import SeparatorBorder from '@/components/shared/SeparatorBorder.vue'
import FeaturedCollectionsSection from '@/components/lab-experience/FeaturedCollectionsSection.vue'
import ContentFilters from '@/components/search/filters/ContentFilters.vue'
import { debounce, throttle } from 'lodash'
import { isElementVisible } from '@/helpers'
import FilterTags from '@/components/shared/FilterTags.vue'

export default {
  name: 'CollectionDetail',
  components: {
    SkillDiveLabRow,
    PillBadge,
    Difficulty,
    FilterIcon,
    ArrowLeftIcon,
    LockIcon,
    LearningAreaLabel,
    SearchInput,
    ActionLoader,
    FeaturedCollectionsSection,
    SeparatorBorder,
    ContentFilters,
    FilterTags,
  },
  mixins: [collectionCommons, licenseAddonCommons, windowResize],
  metaInfo() {
    return {
      title: `INE - Lab Skill Dive Collection`,
      meta: [
        {
          vmid: 'robots',
          name: 'robots',
          content: 'noindex',
        },
      ],
    }
  },
  data() {
    return {
      searchQuery: null,
      sortingSelected: 0,
      relatedCollections: [],
      isLoading: false,
      checkoutUrl: `https://${process.env.VUE_APP_CHECKOUT_URL}`,
      skillDiveEngage: `https://${process.env.VUE_APP_SKILL_DIVE_ENGANGE}`,
      unlockCollapsed: false,
      labsWorker: null,
      labsWorkerState: {
        items: [],
        totalItems: 0,
        activeFilters: {},
        filterCounts: [],
      },
      sortingOptions: [
        { label: 'Recommended', key: 'recomended', value: 'desc' },
        { label: 'Recently added', key: 'published_date', value: 'desc' },
        { label: 'Name (A - Z)', key: 'name', value: 'asc' },
      ],
      debouncedSearchQuery: null,
      throttleScroll: null,
      isFiltersModalOpen: false,
    }
  },
  computed: {
    ...mapState({
      stateQuery: state => state.search.searchQuery,
      collection: state => state.labExperience.currentCollection,
      algoliaKeys: state => state.search.algoliaKeys,
      availablePasses: state => state.auth.available_passes,
      isAuthenticated: state => state.auth.isAuthenticated,
      filterMap: state => state.search.filterMap,
    }),
    ...mapGetters({
      isLabExperienceUser: 'auth/isLabExperienceUser',
      isSkillDiveB2CUser: 'auth/isSkillDiveB2CUser',
      isSkillDiveB2BUser: 'auth/isSkillDiveB2BUser',
      isAccOwnerOrTeamManager: 'auth/isAccOwnerOrTeamManager',
      isBusinessUser: 'business/isBusinessUser',
      businessUser: 'business/getBusinessUser',
      showScheduledBanner: 'app/showScheduledBanner',
      showSkillDive: 'labExperience/showSkillDive',
    }),
    isContentFiltered() {
      return Object.values(this.labsWorkerState.activeFilters).flat().length
    },
    lockedLearningAreas() {
      const las = this.collection?.learning_areas?.map(la => la.name)
      return las?.filter(
        la => this.availableLearningAreas.findIndex(l => l.name === la) < 0
      )
    },
    isLockedContent() {
      return this.lockedContent(this.collection)
    },
    partiallyLockedContent() {
      const las = this.collection.learning_areas?.length
      return (
        this.lockedLearningAreas?.length > 0 &&
        this.lockedLearningAreas?.length < las
      )
    },
    totalCollapsedNavbarHeight() {
      const rootStyles = window.getComputedStyle(document.documentElement)

      let totalHeight = this.extractNumbers(
        rootStyles.getPropertyValue('--navbar-height')
      )

      if (!this.isSkillDiveB2BUser) {
        totalHeight += this.extractNumbers(
          rootStyles.getPropertyValue('--skill-dive-banner-height')
        )
      }

      if (this.showScheduledBanner) {
        if (this.isTablet) {
          totalHeight += this.extractNumbers(
            rootStyles.getPropertyValue('--schedule-banner-height-mobile')
          )
        } else {
          totalHeight += this.extractNumbers(
            rootStyles.getPropertyValue('--schedule-banner-height')
          )
        }
      }
      return totalHeight
    },
  },
  watch: {
    stateQuery: {
      handler(query) {
        if (this.debouncedSearchQuery) {
          this.debouncedSearchQuery(query)
        }
      },
    },
  },
  async created() {
    await this.getContent(this.$route.params.id)

    this.debouncedSearchQuery = debounce(query => {
      this.labsWorker.postMessage([
        {
          type: 'search',
          payload: query,
        },
        {
          type: 'setPage',
          payload: 1,
        },
      ])
    }, 300)
  },
  async beforeMount() {
    if (!this.showSkillDive) {
      this.$router.push({ name: 'home' })
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
  },
  destroyed() {
    window.removeEventListener('scroll', () => this.handleScroll)
  },
  beforeDestroy() {
    const { labsWorker, debouncedSearchQuery, throttleScroll } = this
    if (labsWorker) {
      this.labsWorker.terminate()
    }
    if (debouncedSearchQuery) debouncedSearchQuery.cancel()
    if (throttleScroll)
      window.removeEventListener('scroll', this.throttleScroll)
  },
  methods: {
    ...mapActions('app', ['setError']),
    ...mapActions('labs', ['getRunningLab']),
    ...mapActions('labExperience', [
      'getCollectionById',
      'getVulnerabilitiesCollectionHydratated',
    ]),
    updateFilterAndSearch(filter) {
      this.labsWorker.postMessage([
        {
          type: 'toggleFilter',
          payload: {
            key: filter.facet,
            value: filter.value,
          },
        },
        {
          type: 'setPage',
          payload: 1,
        },
      ])
    },
    handleLazyLoad() {
      this.throttleScroll = throttle(() => {
        const labRows = this.$refs.labRows || []
        const lastRow = labRows.length ? labRows[labRows.length - 1].$el : null
        const { items, totalItems } = this.labsWorkerState
        const canLoadMore = items.length < totalItems

        if (lastRow && isElementVisible(lastRow) && canLoadMore) {
          this.labsWorker.postMessage([
            {
              type: 'loadMore',
            },
          ])
        }
      }, 100)
      window.addEventListener('scroll', this.throttleScroll)
    },
    getCollectionLockedLabel() {
      if (this.isSkillDiveB2CUser) {
        return `This collection is part of the ${this.collectionLearningAreaName} learning area.`
      } else {
        return 'This collection is locked.'
      }
    },
    updateSort(sort) {
      this.sortingSelected = this.sortingOptions.findIndex(
        option => option.key === sort.key
      )

      this.labsWorker.postMessage([
        {
          type: 'setSort',
          payload: sort,
        },
        {
          type: 'setPage',
          payload: 1,
        },
      ])
    },
    getCollectionPartiallyLockedLabel() {
      if (this.lockedLearningAreas?.length === 1) {
        return `the ${this.lockedLearningAreas[0]} Learning Area.`
      }

      return `${this.lockedLearningAreas?.join(', ')} Learning Areas.`
    },
    clearFilters() {
      this.labsWorker.postMessage([
        {
          type: 'clearFilters',
        },
        {
          type: 'setPage',
          payload: 1,
        },
      ])
    },
    async getAccess() {
      if (this.isSkillDiveB2CUser) {
        this.$router.push({
          name: 'user-dashboard',
          params: {
            mode: 'subscriptions',
          },
        })
      } else if (this.isBusinessUser && !this.isAccOwnerOrTeamManager) {
        if (!this.isLoading) {
          this.isLoading = true
          await this.requestAddonLicense('skill-dive')
          this.isLoading = false
        }
      } else {
        window.location.href = this.isAccOwnerOrTeamManager
          ? this.skillDiveEngage
          : this.checkoutUrl
      }
    },
    updateLabsWorkerState(event) {
      const updatedState = event.data

      updatedState.filterCounts.forEach(filter => {
        const { name, data } = filter

        switch (name) {
          case 'year':
            filter.data = data.sort(([a], [b]) => b - a)
            break
          case 'severity':
            filter.data = ['critical', 'high', 'medium', 'low'].reduce(
              (acc, severityKey) => {
                const found = data.find(([key]) => key === severityKey)
                if (found) {
                  acc.push(found)
                }
                return acc
              },
              []
            )
            break
          case 'classification':
            filter.data = [
              'cognitive',
              'governance',
              'healthcare',
              'economic',
              'communication',
              'technical',
            ].reduce((acc, classificationKey) => {
              const found = data.find(([key]) => key === classificationKey)
              if (found) {
                acc.push(found)
              }
              return acc
            }, [])
            break
        }
      })

      this.labsWorkerState = updatedState
    },
    initializeWorker() {
      this.labsWorker = new Worker('@/workers/listItems/worker.js', {
        type: 'module',
      })

      this.labsWorker.onmessage = this.updateLabsWorkerState
      this.labsWorker.onerror = console.error

      if (this.isVulnerabilityColl) {
        this.sortingOptions.push({
          label: 'CVE year',
          key: 'year',
          value: 'desc',
        })
      }

      this.labsWorker.postMessage([
        {
          type: 'init',
          payload: {
            items: this.labs,
            filterKeys: ['severity', 'year', 'tags.name'],
            sourceSort: this.sortingOptions[0],
            searchKeys: ['name', 'cve', 'description_html', 'tags.name'],
            itemsPerPage: 20,
            useLazyLoad: true,
          },
        },
      ])

      this.handleLazyLoad()
    },
    async getContent(id) {
      await this.fetchCollection(id)
      this.getLabsFromCollection(this.collection)
      this.initializeWorker()
      if (this.collection.related_collections.length) {
        this.fetchRelatedCollections()
      }

      if (this.isAuthenticated) {
        this.getRunningLab()
      }
    },
    async fetchRelatedCollections() {
      const {
        algoliaKeys: { appKey, appId, searchIndex },
        collection: { related_collections },
      } = this
      const client = algoliasearch(appId, appKey)
      const index = client.initIndex(searchIndex)

      try {
        const { results } = await index.getObjects(
          related_collections.map(collection => collection.id)
        )
        this.relatedCollections = results
      } catch (error) {
        this.setError(error)
      }
    },
    async fetchCollection(id) {
      try {
        this.isLoading = true
        const result = await this.getCollectionById(id)
        if (!result || !result.id) {
          this.$router.push({
            name: 'NotFound',
            params: { pathMatch: this.$route.path },
          })
          throw new Error('NotFound')
        }
      } catch (error) {
        this.setError(error)
      } finally {
        this.isLoading = false
      }
    },
    handleScroll(event) {
      const unlockBanner = document.querySelector(
        '.collection-detail__locked-badge'
      )
      event.preventDefault()
      if (window.scrollY >= 20) {
        this.unlockCollapsed = true
        unlockBanner?.classList?.add(
          'collection-detail__locked-badge--collapse'
        )
      } else {
        this.unlockCollapsed = false
        unlockBanner?.classList?.remove(
          'collection-detail__locked-badge--collapse'
        )
      }
    },
    extractNumbers(value) {
      const regex = /\d+/
      return Number(value.match(regex))
    },
  },
}
</script>

<style lang="scss" scoped>
.collection-detail {
  background-image: url('../assets/images/backgrounds/skill-dive-bg.jpg');
  background-size: 100%;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  flex-direction: column;
  margin: 0 auto;
  min-height: 100vh;

  padding-bottom: 40px;

  @include tablet {
    padding-bottom: 50px;
  }
  @include desktop {
    padding-bottom: 120px;
  }
  &--vulnerabilities-bg-1 {
    background: var(--dark-brighter);
    @include mobile {
      background: #000;
      background-image: var(--vulnerabilities-bg-1);
      background-repeat: no-repeat;
      background-position: right top -150px;
    }
  }
  &__item-with-separator {
    @include mobile {
      &:not(:last-child)::after {
        content: ' ';
        width: 1px;
        height: 28px;
        opacity: 0.5;
        background: #fff;
        margin: 0 10px;
      }
    }
  }

  &__locked-badge {
    display: flex;
    color: var(--white);
    width: 100%;
    padding: 10px 30px;
    justify-content: center;
    align-items: center;
    gap: 5px;
    background: var(--paypal-gradient);
    position: fixed;
    z-index: 200;
    &-link {
      text-decoration: underline;
      cursor: pointer;
      &--disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }

    &-partial {
      font-size: 10px;
      line-height: 12px;
      padding: 10px 15px;
      gap: 10px;

      @include mobile {
        font-size: 12px;
        line-height: 14px;
        padding: 10px 30px;
      }

      svg {
        min-width: 16px;
      }
    }
  }
  &__container {
    max-width: 1240px;
    width: 100%;
    padding: 0 20px;

    @include mobile {
      padding: 0 30px;
    }

    @include desktop {
      padding: 0 100px;
    }
  }
  &__navigation {
    display: flex;
    padding: 8px 16px;
    justify-content: flex-start;
    align-items: center;
    margin: 20px 0 40px;
  }
  &__header {
    display: flex;
    flex-direction: column;
    gap: 20px;
    align-items: center;
    margin-bottom: 50px;
    color: var(--white);

    &-title {
      text-align: center;

      font-size: rem(32px);
      font-style: normal;
      font-weight: 700;
      line-height: rem(38px);

      @include mobile {
        font-size: 40px;
        line-height: 52px;
      }
    }

    &-subtitle {
      display: flex;
      align-items: center;
      width: 100%;
      gap: 15px;
      flex-flow: column;
      white-space: nowrap;

      @include mobile {
        flex-flow: row;
        justify-content: center;
        flex-wrap: wrap;
      }
      @include desktop {
        flex-wrap: unset;
      }

      &-disclaimer {
        color: var(--positive-light);
        font-size: rem(14px);
        font-style: normal;
        font-weight: 700;
        line-height: rem(20px);
        @include mobile {
          flex-basis: 100%;
          text-align: center;
        }
        @include desktop {
          flex-basis: unset;
          text-align: inherit;
        }
      }
      &-details {
        display: flex;
        gap: 10px;
      }
      &-learning-area {
        position: relative;
        left: -3px;
        &-item {
          display: flex;
          align-items: center;
          gap: 15px;
        }
        &-wrapper {
          @include mobile {
            display: flex;
            gap: 15px;
          }
        }
      }
      &-separator {
        &--desktop-only {
          display: none;
          @include desktop {
            display: block;
          }
        }
      }
    }

    &-description {
      @include mobile {
        text-align: center;
      }
    }
  }
  &__search {
    display: flex;
    gap: 10px;
    flex-direction: column;
    margin-bottom: 5px;

    @include mobile {
      margin-bottom: 10px;
    }

    &-input {
      gap: 10px;
    }
  }
  &__results-label {
    margin-bottom: 5px;

    color: var(--white);
    font-family: var(--font-bold);
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 700;
    line-height: 1.25rem; /* 142.857% */
  }
  &__filter-pills {
    display: flex;
    align-items: center;
    min-height: 40px;

    margin-bottom: 5px;

    @include mobile {
      margin-bottom: 10px;
    }
  }
  &__content {
    display: flex;
    flex-direction: column;
    gap: 40px;
    width: 100%;

    @include desktop {
      flex-direction: row;
      padding-bottom: 40px;
    }

    &-filters {
      &-container {
        display: none;

        @include mobile {
          display: initial;
        }

        @include desktop {
          width: 250px;
        }

        &--modal-open {
          display: initial;
          position: fixed;
          top: 0;
          left: 0;
          z-index: 99999;
          background-color: var(--black-50);
          backdrop-filter: blur(35px);
          width: 100%;
          height: 100%;
          padding: 10px;
        }
      }

      &-btn {
        position: fixed;
        bottom: 50%;
        transform: translateY(50%);
        right: 20px;
        z-index: 1;
        width: 40px;
        height: 40px;

        @include mobile {
          display: none;
        }

        &::after {
          opacity: 0;
          content: '';
          position: absolute;
          top: -2px;
          right: -2px;
          width: 13px;
          height: 13px;

          border-radius: 50%;
          background: var(--primary-ine-gradient);
        }
        &--filtered {
          &::after {
            opacity: 1;
          }
        }
      }
      &-mobile-header {
        display: flex;
        justify-content: space-between;
        margin-bottom: 20px;
        height: 40px;
        @include mobile {
          display: none;
        }
      }
    }
    &-labs-list {
      &-ul {
        display: flex;
        flex: 1;
        row-gap: 10px;
        flex-flow: column;
        margin-bottom: 70px;
        padding-left: 0;
      }

      &-empty {
        display: flex;
        align-items: center;
        flex-flow: column;
        padding: 20px;
        min-height: 100vh;

        &__img {
          width: 280px;
        }
        &__error {
          margin: -30px 0 0 0;
          color: var(--white);
          font-family: var(--font-bold);
          font-style: normal;
          font-weight: 700;
          font-size: rem(20px);
          line-height: rem(26px);
        }
        &__instruction {
          color: var(--light-blue-gray);
          font-family: var(--font-book);
          font-style: normal;
          font-weight: 450;
          font-size: rem(16px);
          line-height: rem(24px);
        }
      }

      &-browse {
        max-width: 144px;
      }
    }
  }
  &__related-collections {
    padding: 0;
  }
  &__loader {
    margin: auto;
    width: 100%;
    min-height: 300px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
